import type { LiveToken } from './types'
import styles from './live.module.css'

export interface MapLegendProps {
  tokens: readonly LiveToken[]
  selectedId: string | null
  onSelect: (tokenId: string) => void
  /** Dead tokens are marked here too, since this is where a name is actually readable. */
  canControl: boolean
}

/**
 * Who is on the map, under the map.
 *
 * A token is a circle roughly 44 px across; at that size a portrait is recognisable and a *name*
 * is not. So the map answers "where is everyone" and this answers "who is that one" — which is
 * the question a Game Master asks about the third identical ghoul.
 *
 * Selecting here selects on the map, because the two are the same list seen twice.
 */
export function MapLegend({ tokens, selectedId, onSelect, canControl }: MapLegendProps) {
  if (tokens.length === 0) {
    return <p className={styles.note}>Nothing on the map yet.</p>
  }

  // Sides together, then by name: a Game Master reads this looking for a side, and an
  // arrival-ordered list scatters them.
  //
  // Numeric collation, because half the names on this list now end in a number: plain
  // `localeCompare` puts Zombie 10 between Zombie 1 and Zombie 2, which is the one ordering a
  // Game Master scanning for "the one I said was Zombie 9" cannot use.
  const ordered = [...tokens].sort(
    (a, b) =>
      (a.side ?? '').localeCompare(b.side ?? '') ||
      a.label.localeCompare(b.label, undefined, { numeric: true }),
  )

  return (
    <ul className={styles.legend} aria-label="On the map">
      {ordered.map((token) => (
        <li key={token.tokenId}>
          <button
            type="button"
            className={[
              styles.legendItem,
              selectedId === token.tokenId ? styles.legendSelected : '',
              token.isDead ? styles.legendDead : '',
            ].join(' ')}
            aria-pressed={selectedId === token.tokenId}
            onClick={() => onSelect(token.tokenId)}
          >
            {/* The legend is where the third identical ghoul stops being ambiguous, so the
                number goes on the face here too — a corner badge rather than the numeral the map
                draws, because at 28px beside a readable name it only has to confirm. */}
            <span className={styles.numbered}>
              {token.portraitUrl != null ? (
                <img
                  className={styles.legendPortrait}
                  src={token.portraitUrl}
                  alt=""
                  loading="lazy"
                />
              ) : (
                <span className={styles.legendInitial} aria-hidden="true">
                  {token.label.slice(0, 1).toUpperCase()}
                </span>
              )}

              {token.ordinal !== null && (
                <span className={styles.ordinalBadge} aria-hidden="true">
                  {token.ordinal}
                </span>
              )}
            </span>

            <span className={styles.legendText}>
              <span className={styles.legendName}>{token.label}</span>
              {(token.side !== null || token.isDead || token.hidden) && (
                <span className={styles.rowMeta}>
                  {[
                    token.side,
                    token.isDead ? 'dead' : null,
                    // Only the Game Master is ever sent a hidden token, so this line only ever
                    // renders on their screen — but say it plainly rather than relying on that.
                    canControl && token.hidden ? 'hidden' : null,
                  ]
                    .filter((part) => part !== null && part !== '')
                    .join(' · ')}
                </span>
              )}
            </span>
          </button>
        </li>
      ))}
    </ul>
  )
}
